import { Close } from '@mui/icons-material';
import { Dialog, DialogActions, DialogContent, IconButton, type Theme, styled, useTheme } from '@mui/material';
import { type SystemStyleObject } from '@mui/system';
import { merge } from 'lodash-es';
import { type ReactNode } from 'react';
import { isDeviceMobile } from '#helpers/uaParser.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';

const DialogTitle = styled('div')(({ theme }) => ({
  color: theme.palette.grey[100],
  flex: '0 0 auto',
  margin: 0,
  padding: theme.spacing(2),
}));

const TitleContainer = styled('div')({
  paddingRight: '40px',
  width: '100%',
});

export type Props = {
  children: ReactNode;
  dialogContentSx?: SystemStyleObject<Theme>;
  disableBackdropClick?: boolean;
  id: string | number;
  onClose: () => void;
  open: boolean;
  sx?: SystemStyleObject<Theme>;
  title: ReactNode;
  width?: string;
};

export const Modal = ({
  children,
  dialogContentSx,
  disableBackdropClick,
  id,
  onClose,
  open = false,
  sx = {},
  title,
  width = '100%',
}: Props) => {
  const { uaParser } = useToolbelt();
  const theme = useTheme();
  const isMobileInLandscape = globalThis.outerHeight < globalThis.outerWidth && isDeviceMobile(uaParser);

  const baseStyles = {
    '.MuiDialog-paper': {
      backgroundColor: theme.palette.grey[900],
      width,
      [theme.breakpoints.down('md')]: {
        margin: theme.spacing(2),
      },
    },
  };

  return (
    <Dialog
      aria-labelledby={`${String(id)}-video-title`}
      id={`${String(id)}-video`}
      maxWidth="lg"
      onClose={(_event, reason) => {
        if (disableBackdropClick && reason === 'backdropClick') {
          return;
        }

        onClose();
      }}
      open={open}
      sx={merge({}, baseStyles, sx)}
    >
      <DialogTitle
        id={`${String(id)}-video-title`}
        sx={{
          alignItems: 'center',
          display: 'flex',
          height: '56px',
          justifyContent: 'space-between',
          paddingBottom: 0,
          paddingTop: 0,
        }}
      >
        <TitleContainer>{title}</TitleContainer>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: theme.palette.grey[100],
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          borderBottom: isMobileInLandscape ? 'none' : undefined,
          borderColor: theme.palette.grey[800],
          width: '100%',
          ...dialogContentSx,
        }}
      >
        {children}
      </DialogContent>
      <DialogActions
        sx={{
          display: isMobileInLandscape ? 'none' : undefined,
          [theme.breakpoints.down('sm')]: {
            display: 'none',
          },
        }}
      />
    </Dialog>
  );
};
